import React, { useEffect, useMemo, useRef, useState } from 'react';

type LocationSuggestInputProps = {
  id: string;
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
  placeholder?: string;
  inputClassName: string;
  /** When true, dropdown was opened but there are no values in the dataset */
  noDataHint?: string;
  /** Shown when the user typed something but nothing matches */
  noMatchesHint?: string;
  maxShown?: number;
};

/**
 * Combobox-style suggestions: filters in real time as the user types (client-side only).
 * Replaces <datalist>, which is inconsistent across browsers while typing.
 */
export function LocationSuggestInput({
  id,
  value,
  onChange,
  suggestions,
  placeholder,
  inputClassName,
  noDataHint,
  noMatchesHint,
  maxShown = 60,
}: LocationSuggestInputProps) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listId = `${id}-listbox`;

  const needle = value.trim().toLowerCase();

  const filtered = useMemo(() => {
    const unique = [...new Set(suggestions.map((s) => s.trim()).filter(Boolean))];
    unique.sort((a, b) => a.localeCompare(b));
    if (!needle) {
      return unique.slice(0, maxShown);
    }
    const hit = unique.filter((s) => s.toLowerCase().includes(needle));
    return hit.slice(0, maxShown);
  }, [suggestions, needle, maxShown]);

  useEffect(() => {
    setHighlight((h) => {
      if (filtered.length === 0) return 0;
      return Math.min(h, filtered.length - 1);
    });
  }, [filtered.length, needle]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (s: string) => {
    onChange(s);
    setOpen(false);
  };

  const showNoMatches =
    open &&
    needle.length > 0 &&
    filtered.length === 0 &&
    suggestions.length > 0;
  const showPanel =
    open &&
    (filtered.length > 0 ||
      showNoMatches ||
      (suggestions.length === 0 && noDataHint));

  return (
    <div ref={wrapRef} className="relative">
      <input
        id={id}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!open) {
              setOpen(true);
              return;
            }
            if (filtered.length === 0) return;
            setHighlight((h) => Math.min(filtered.length - 1, h + 1));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!open) {
              setOpen(true);
              return;
            }
            if (filtered.length === 0) return;
            setHighlight((h) => Math.max(0, h - 1));
            return;
          }
          if (e.key === 'Enter' && open && filtered[highlight] != null) {
            e.preventDefault();
            pick(filtered[highlight]);
            return;
          }
          if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
        className={inputClassName}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
      />
      {showPanel ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute left-0 right-0 top-full z-[100] mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg ring-1 ring-black/5"
        >
          {filtered.length > 0
            ? filtered.map((s, i) => (
                <li
                  key={`${s}-${i}`}
                  role="option"
                  aria-selected={i === highlight}
                  className={[
                    'cursor-pointer px-3 py-2 text-sm',
                    i === highlight
                      ? 'bg-brand-500/15 text-slate-900'
                      : 'text-slate-700 hover:bg-slate-50',
                  ].join(' ')}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => pick(s)}
                >
                  {s}
                </li>
              ))
            : showNoMatches
              ? (
                  <li className="px-3 py-2 text-xs text-slate-500">
                    {noMatchesHint}
                  </li>
                )
              : (
                  <li className="px-3 py-2 text-xs text-slate-500">
                    {noDataHint}
                  </li>
                )}
        </ul>
      ) : null}
    </div>
  );
}
